import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate, Navigate, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import moment from 'moment';
import styled from 'styled-components';
import { StatusCard } from '../../../components/StatusCard';
import { EmptyState } from '../../../components/EmptyState';
import PackageDeliveryAlert from '../../../components/PackageDeliveryAlert';
import AppLayout from '../../../layouts/AdminLayout';
import { Dialog, Transition } from '@headlessui/react';
import { ArrowLeftIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { OrderData } from './OrderDetail';
import { AnalyticCard } from '../../../components/AnalyticCard';
import { TableData } from '../../../components/TableData';
import { getStatusType } from '../../../utils/status';
import { calculateCreditsFromWeight } from '../../../utils/zone';
import toast from 'react-hot-toast';
import { useRoles } from '../../../hooks/useRoles';

const TABLE_HEAD = [
	'Waybill No.',
	'Recipient Name',
	'Product Weight',
	'Credit used',
	'Status',
	'Date Delivered',
	'More',
];

const STATUS_OPTIONS = [
	{ label: 'All', value: '' },
	{ label: 'Picked up', value: 'PICKED_UP' },
	{ label: 'Processing', value: 'PROCESSING' },
	{ label: 'Picked up Scheduled', value: 'PICKED_UP_SCHEDULED' },
];

export default function MultipleOrderDetails() {
	const { orderID } = useParams<{ orderID: string }>();
	const { scope } = useRoles();
	const location = useLocation();
	const navigate = useNavigate();
	const Baseurl = process.env.REACT_APP_BASEURL;
	const token = JSON.parse(localStorage.getItem('eftd-admin-token') || '{}')?.data?.data?.token;
	const [isLoadingPackage, setIsLoadingPackage] = useState(false);
	const [isRequesting, setIsRequesting] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const [isRefundOpen, setIsRefundOpen] = useState(false);
	const [isStatusOpen, setIsStatusOpen] = useState(false);
	const [isStatusUpdating, setIsStatusUpdating] = useState(false);
	const [selectedStatus, setSelectedStatus] = useState('');

	const [topUpAmount, setTopUpAmount] = useState('');
	const [isTopupRequestedOpen, setIsTopupRequestedOpen] = useState(true);
	const [isCancelling, setIsCancelling] = useState(false);
	const [isRemoving, setIsRemoving] = useState(false);
	const [cancellingWaybill, setCancellingWaybil] = useState('');
	const [removingWaybill, setRemovingWaybill] = useState('false');
	const [reassigningWaybill, setReassigningWaybill] = useState('');
	const [reassigning, setReassigning] = useState(false);
	const [isCancelOpen, setIsCancelOpen] = useState(false);
	const [tab, setTab] = useState('new');
	const [localQueueItems, setLocalQueueItems] = useState<any[]>([]);
	const [orderStats, setOrderStats] = useState({
		failed: 0,
		delivered: 0,
		total: 0,
	});
	const [currentDeliveryPage, setCurrentDeliveryPage] = useState(1);
	const deliveryItemsPerPage = 10;
	const [deliveryPagination, setDeliveryPagination] = useState({
		page: 1,
		limit: 10,
		total: 0,
	});

	const fetchQueue = async () => {
		const response = await axios.get(
			`${Baseurl}/api/admin/orders/${orderID}/topups/queue?page=1&limit=100`,
			config,
		);
		const queue = response?.data?.data.items.filter((data: any) => data.orderId === orderID);
		setLocalQueueItems(queue[0]?.items);
		return response?.data?.data;
	};

	const updateOrderStatus = async (status: string) => {
		if (!status) {
			toast.error('Please select a status');
			return;
		}

		setIsStatusUpdating(true);
		try {
			await axios.post(
				`${Baseurl}/api/admin/orders/${orderID}/update/status`,
				{ status },
				{
					...config,
				},
			);
			toast.success('Order status updated successfully');
			setIsStatusOpen(false);
			setSelectedStatus(''); // Reset selection
			refetch();
		} catch (error: any) {
			const errorMessage = error.response?.data?.message;

			if (Array.isArray(errorMessage)) {
				toast.error(errorMessage[0]);
			} else if (typeof errorMessage === 'string') {
				toast.error(errorMessage);
			} else {
				toast.error('Failed to update order status');
			}
		} finally {
			setIsStatusUpdating(false);
		}
	};

	const {
		data: queueData,
		isLoading: isLoadingQueue,
		isRefetching: isRefetchingQueue,
		refetch: refetchQueue,
	} = useQuery<any>(['QUEUE', orderID], fetchQueue, {
		enabled: !!orderID,
		cacheTime: 0,
	});

	const config = {
		headers: { Authorization: `Bearer ${token}` },
	};

	const fetchOrder = async () => {
		const response = await axios.get(
			`${Baseurl}/api/admin/orders/${orderID}?page=${deliveryPagination.page}&limit=${deliveryPagination.limit}`,
			config,
		);
		setOrderStats(response?.data?.data?.stats);
		setDeliveryPagination((prev) => ({
			...prev,
			total: response?.data?.data?.meta?.total || 0,
		}));
		return response?.data?.data.items;
	};

	const {
		data: orderData,
		isLoading,
		isRefetching,
		refetch,
	} = useQuery<OrderData>(['ORDER', orderID], fetchOrder, {
		enabled: !!orderID,
		cacheTime: 0,
	});

	const status = orderData ? getStatusType(orderData.orderStatus) : { title: '', type: '' };

	const firstDeliveryDetail = orderData?.deliveryDetails?.[0] || {
		trackingLink: undefined,
		fullAddress: undefined,
		phoneNumber: undefined,
		recipientName: undefined,
		itemWeight: undefined,
		itemDescription: undefined,
		additionalDetails: undefined,
		deliveryDate: undefined,
		timelines: undefined,
	};
	const pickUpDetail = orderData?.pickUpDetail || {
		fullAddress: undefined,
		phoneNumber: undefined,
		senderName: undefined,
		riderInfo: undefined,
		timelines: undefined,
	};

	const copyToClipboard = (text: string) => {
		navigator.clipboard.writeText(text);
	};

	const packagedAtHub = async () => {
		setIsLoadingPackage(true);
		try {
			await axios.post(
				`${Baseurl}/api/admin/orders/${orderID}/update/status`,
				{ status: 'IN_HUB' },
				{
					...config,
				},
			);
			refetch();
		} catch (error) {
		} finally {
			setIsLoadingPackage(false);
		}
	};

	const cancelOrder = async () => {
		setIsCancelling(true);
		try {
			await axios.patch(
				`${Baseurl}/api/admin/orders/${orderID}/cancel`,
				{},
				{
					...config,
				},
			);
			refetch();
		} catch (error) {
		} finally {
			setIsCancelling(false);
		}
	};

	const requestTopup = async () => {
		setIsRequesting(true);
		try {
			await axios.post(
				`${Baseurl}/api/admin/orders/${orderID}/topups`,
				{
					queueId: queueData?.items?.find((data: any) => data.orderId === orderID).id,
				},
				{
					...config,
				},
			);
			refetchQueue();
			refetch();

			setIsOpen(false);
		} catch (error) {
		} finally {
			setIsRequesting(false);
		}
	};
	const reassignWaybill = async (refId: string) => {
		setReassigning(true);
		setReassigningWaybill(refId);
		try {
			await axios.post(
				`${Baseurl}/api/admin/orders/${orderID}/${refId}/retry-create-task`,
				{},
				{
					...config,
				},
			);
			toast.success('Task created');
			refetch();
		} catch (error) {
		} finally {
			setReassigning(false);
			setReassigningWaybill('');
		}
	};

	const removeQueueData = async (waybill: string) => {
		setRemovingWaybill(waybill);
		setIsRemoving(true);
		try {
			await axios.patch(
				`${Baseurl}/api/admin/orders/${orderID}/topups/${waybill}/remove`,
				{},
				{
					...config,
				},
			);
			setLocalQueueItems((prevItems) => prevItems.filter((item) => item.waybillNumber !== waybill));
			refetchQueue();
		} catch (err: any) {
			console.error('Error fetching order details:', err);
		} finally {
			setRemovingWaybill('');
			setIsRemoving(false);
		}
	};

	const cancelQueueData = async (waybill: string) => {
		setCancellingWaybil(waybill);
		setIsCancelling(true);
		try {
			await axios.patch(
				`${Baseurl}/api/admin/orders/${orderID}/topups/${waybill}/cancel`,
				{},
				{
					...config,
				},
			);
			setLocalQueueItems((prevItems) => prevItems.filter((item) => item.waybillNumber !== waybill));

			refetchQueue();
		} catch (err: any) {
			console.error('Error fetching order details:', err);
		} finally {
			setCancellingWaybil('');
			setIsCancelling(false);
		}
	};

	const processRefund = async () => {
		setIsRequesting(true);
		try {
			await axios.patch(
				`${Baseurl}/api/admin/orders/${orderID}/items/${undefined}/refund/initiate`,
				{},
				{
					...config,
				},
			);
			refetch();
		} catch (error) {
		} finally {
			setIsRequesting(false);
		}
	};

	const completeRefund = async () => {
		setIsRequesting(true);
		try {
			await axios.post(
				`${Baseurl}/api/admin/orders/${orderID}/update/status`,
				{ status: 'REFUND_COMPLETED' },
				{
					...config,
				},
			);
			refetch();
			setIsRefundOpen(false);
		} catch (error) {
		} finally {
			setIsRequesting(false);
		}
	};

	useEffect(() => {
		if (orderID) {
			refetch();
		}
	}, [orderID, refetch, deliveryPagination.page, deliveryPagination.limit]);

	const filteredItems =
		localQueueItems?.filter((item: any) => (tab === 'new' ? item.label === 'new' : item.label === 'sent')) ||
		[];

	const totalWeight = filteredItems.reduce((total: number, item: any) => total + (item.weightDiff || 0), 0);

	const totalAmount = filteredItems.reduce((total: number, item: any) => total + (item.amount || 0), 0);

	return (
		<AppLayout>
			{isLoading ? (
				<div className="p-10 text-center text-gray-500">Loading...</div>
			) : !orderData ? (
				<EmptyState text="Order not found" type="" />
			) : (
				<div className="p-5">
					<div className="flex !items-center !justify-between !p-4">
						<button className="flex items-center gap-2 !mb-6" onClick={() => navigate(-1)}>
							<ArrowLeftIcon width={24} height={24} color="#666D80" />
							<p className="!text-sm text-[#666d80]">Go Back</p>
						</button>
					</div>

					<div className="flex justify-between items-cented mb-5">
						<h2 className="text-3xl font-bold">Order Details</h2>

						<div className="flex items-center gap-4">
							<HubButton
								disabled={isLoadingPackage}
								onClick={() => {
									navigate(`/orders/receipt/${orderID}`);
								}}
								className="!border !border-[#fe7f1f] !rounded-[10px] !bg-transparent !text-[#fe7f1f] !font-bold !py-4 !px-7">
								View Receipt
							</HubButton>
							<HubButton
								onClick={() => {
									setIsStatusOpen(true);
								}}
								className="!border !border-[#fe7f1f] !rounded-[10px] !bg-transparent !text-[#fe7f1f] !font-bold !py-4 !px-7">
								Change Status
							</HubButton>
							{orderData?.orderStatus === 'PACKAGE_AT_HUB' && scope === 'write' && (
								<HubButton
									disabled={isLoadingPackage}
									onClick={() => {
										packagedAtHub();
									}}
									className="!bg-[#fe7f1f] !rounded-[10px] !text-white !font-bold !py-4 !px-7">
									{isLoadingPackage ? 'Loading...' : 'Package at Hub'}
								</HubButton>
							)}

							{/* {qudata && ( */}
							{localQueueItems && localQueueItems.length > 0 && scope === 'write' && (
								<HubButton
									onClick={() => {
										setIsOpen(true);
									}}
									className="!bg-[#fe7f1f] !rounded-[10px] !text-white !font-bold !py-4 !px-7 disabled:!bg-[#fe7f1f]/50 disabled:cursor-not-allowed ">
									Preview Top Up Request(
									{localQueueItems.length > 0 ? localQueueItems.length : 0})
								</HubButton>
							)}
						</div>
					</div>

					{orderData?.hasPendingTopupRequest && !orderData?.TopupRequest?.paid && isTopupRequestedOpen && (
						<div className="!flex !items-center justify-between !gap-3 !w-full !rounded-lg !bg-[#FEF3F2] !p-4 !my-4 !border-l-4 !border-l-[#F04438]">
							<div className="flex items-center gap-3">
								<ExclamationTriangleIcon className="!w-5 !h-5 !text-[#F04438]" aria-hidden="true" />
								<p className="!text-[#F04438]">Top up has been requested</p>
							</div>
							<button
								className="text-xs font-semibold !text-[#F04438]"
								onClick={() => setIsTopupRequestedOpen(false)}>
								Close
							</button>
						</div>
					)}

					{orderData?.orderStatus === 'CANCELLED' && orderData.pickUpDetail.status === 'CANCELLED' && (
						<div className="!flex !items-center !justify-between !gap-3 !w-full !rounded-lg !bg-[#FEF3F2] !p-4 !my-4 !border-l-4 !border-l-[#F04438]">
							<div className="!flex !items-center  !gap-3">
								<ExclamationTriangleIcon className="!w-5 !h-5 !text-[#F04438]" aria-hidden="true" />
								<p className="">Order cancelled</p>
							</div>
							{scope === 'write' && (
								<button
									className="py-4 px-6 rounded-[10px] bg-[#ffeed4] border border-[#fe7f1f] text-[#fe7f1f] font-bold text-sm"
									onClick={() => processRefund()}>
									{isRequesting ? 'Loading..' : 'Process refund'}
								</button>
							)}
						</div>
					)}

					{orderData?.orderStatus === 'DELIVERY_FAILED' && (
						<PackageDeliveryAlert id={orderID!} jobId={orderData?.jobId!} />
					)}

					<OrderHeader>
						<OrderInfo>
							<div>
								<InfoLabel>Order ID:</InfoLabel>
								<OrderId>{orderData.orderId}</OrderId>
							</div>
							<StatusContainer>
								<StatusCard title={status.title} type={status.type} />
								<RefreshIcon
									className={`${isRefetching ? 'animate-spin' : ''}`}
									onClick={() => refetch()}
									src="/refresh.svg"
									alt="Refresh"
								/>
							</StatusContainer>
						</OrderInfo>

						<ActionButtons>
							{firstDeliveryDetail?.trackingLink && (
								<div className="relative w-full overflow-x-auto whitespace-nowrap text-sm text-gray-700 pr-10">
									<span className=" border-gray-100 rounded-lg">
										Tracking Link: <strong className="text-black">{firstDeliveryDetail.trackingLink}</strong>
									</span>
									<button
										onClick={() => copyToClipboard(firstDeliveryDetail.trackingLink!)}
										className="absolute top-1/2 right-0 -translate-y-1/2 p-1 hover:bg-gray-100 rounded">
										<img src="/copy.svg" alt="Copy" className="w-4 h-4" />
									</button>
								</div>
							)}
							{(orderData.orderStatus === 'IN_HUB' ||
								orderData.orderStatus === 'PACKAGE_AT_HUB' ||
								orderData.orderStatus === 'PICKUP_SCHEDULED' ||
								orderData.orderStatus === 'PROCESSING') &&
								scope === 'write' && (
									<HubButton
										disabled={isRequesting}
										onClick={() => {
											setIsCancelOpen(true);
										}}
										className="!bg-transparent !border !border-[#fe7f1f] !rounded-[10px] !text-[#fe7f1f] !font-bold !py-3 !px-4 !min-w-max text-sm">
										Cancel
									</HubButton>
								)}
						</ActionButtons>
					</OrderHeader>

					<p className="-my-4 text-sm">
						{orderData.pickUpDetail.note && `Note: ${orderData.pickUpDetail.note}`}
					</p>

					<GridContainer>
						<AnalyticCard amount={orderStats.total} title="Total Deliveries" type="picked" />
						<AnalyticCard amount={orderStats.failed} title="Failed Deliveries" type="total" />
						<AnalyticCard amount={orderStats.delivered} title="Completed Deliveries" type="processing" />

						<InfoCard>
							<RiderHeader>
								<img src="/motorcycle.svg" alt="Motorcycle" />
								{/* <ContactButton className="!flex !justify-center !items-center !p-2 !gap-2.5 !ml-auto !text-sm !font-semibold !bg-white !border !border-orange-500 !text-orange-500 !rounded-[35px]">
							Contact Rider
						  </ContactButton> */}
							</RiderHeader>
							<InfoText>
								<InfoLabelSmall>Rider Information</InfoLabelSmall>
								<InfoValue>{pickUpDetail.riderInfo?.fullName || 'N/A'}</InfoValue>
								<InfoValueSmall>{pickUpDetail.riderInfo?.phoneNumber || 'N/A'}</InfoValueSmall>
							</InfoText>
						</InfoCard>
					</GridContainer>

					<DetailsGrid>
						<DetailCard>
							{orderData.user && (
								<>
									<DetailHeader>
										<DetailTitle>Account Information</DetailTitle>
									</DetailHeader>

									<DetailText className="flex gap-1">
										Account Name:{' '}
										<DetailTextBold>
											{orderData?.user?.businessName ||
												`${orderData.user.firstName} ${orderData.user.lastName}`}
										</DetailTextBold>
									</DetailText>
									<DetailText className="flex gap-1">
										Account Type: <DetailTextBold>{orderData?.user?.accountType}</DetailTextBold>
									</DetailText>
									<hr className="border-gray-400 my-2 " />
								</>
							)}

							<DetailHeader>
								<DetailTitle>Pickup Information</DetailTitle>
							</DetailHeader>
							<DetailContent>
								<DetailTextBold>{pickUpDetail?.senderName}</DetailTextBold>
								<DetailText>{pickUpDetail?.fullAddress}</DetailText>
								<DetailText>{pickUpDetail?.phoneNumber}</DetailText>
								<DetailText>{orderData?.email || ''}</DetailText>
								<DetailText>
									Service Type -{' '}
									<strong>
										{orderData.deliveryMethod === 'HUB_AND_SPOKE'
											? 'Hub and Spoke'
											: orderData.deliveryMethod}
									</strong>
								</DetailText>
								{/* <DetailText>
                  Delivery Type - <strong>{orderData.deliveryType}</strong>
                </DetailText>
                <DetailText>
                  Waybill number - <strong>Nil</strong>
                </DetailText> */}
							</DetailContent>
						</DetailCard>

						<div className="!py-5 !px-8 !border !border-black/10 !rounded-[10px] !col-span-1 !row-span-2">
							<p className="!text-sm !text-gray-400 !font-semibold !mb-6">Pickup Timeline</p>
							<div className="!flex !justify-between !items-center !mb-7">
								<div className="!flex !gap-6 !items-center">
									<div
										className={`!text-xs !min-w-[90px] ${
											pickUpDetail?.timelines![0] ? '!text-gray-700' : '!text-gray-200'
										}  !font-semibold `}>
										<p>
											{moment(pickUpDetail?.timelines![0]?.entriedAt).format('DD MMM, YYYY') || 'D MMM, YYYY'}
										</p>
										<p>{moment(pickUpDetail?.timelines![0]?.entriedAt).format('HH:mm') || '12:00'}</p>
									</div>
									<div
										className={`${
											pickUpDetail?.timelines![0] ? '!text-gray-700' : '!text-gray-200'
										}  !font-semibold `}>
										<p className="!text-sm">{pickUpDetail?.timelines![0]?.event || 'Assigned Rider'}</p>
										<p className="!text-[10px] !text-gray-400">{pickUpDetail?.fullAddress}</p>
									</div>
								</div>
								{pickUpDetail?.timelines![0] && <img src="/tick-success.svg" alt="Success" />}
							</div>
							<div className="!flex !justify-between !items-center !mb-7">
								<div className="!flex !gap-6 !items-center">
									<div
										className={`!text-xs !min-w-[90px] ${
											pickUpDetail?.timelines![1] ? '!text-gray-700' : '!text-gray-200'
										}  !font-semibold `}>
										<p>
											{moment(pickUpDetail?.timelines![1]?.entriedAt).format('DD MMM, YYYY') ||
												'DD MMM, YYYY'}
										</p>
										<p>{moment(pickUpDetail?.timelines![1]?.entriedAt).format('HH:mm') || '12:00'}</p>
									</div>
									<div
										className={`${
											pickUpDetail?.timelines![1] ? '!text-gray-700' : '!text-gray-200'
										}  !font-semibold `}>
										<p className="!text-sm">
											{pickUpDetail?.timelines![1]?.event || 'Rider enroute to pickup package'}
										</p>
										<p className="!text-[10px] !text-gray-400">{pickUpDetail?.fullAddress}</p>
									</div>
								</div>
								{pickUpDetail?.timelines![1] && (
									<img
										src={
											pickUpDetail?.timelines![1].event.includes('failed')
												? '/tick-failed.svg'
												: '/tick-success.svg'
										}
										alt=""
									/>
								)}
							</div>
							<div className="!flex !justify-between !items-center !mb-7">
								<div className="!flex !gap-6 !items-center">
									<div
										className={`!text-xs !min-w-[90px] ${
											pickUpDetail?.timelines![2] ? '!text-gray-700' : '!text-gray-200'
										}  !font-semibold `}>
										<p>
											{moment(pickUpDetail?.timelines![2]?.entriedAt).format('DD MMM, YYYY') || 'D MMM, YYYY'}
										</p>
										<p>{moment(pickUpDetail?.timelines![2]?.entriedAt).format('HH:mm') || '12:00'}</p>
									</div>
									<div
										className={`${
											pickUpDetail?.timelines![2] ? '!text-gray-700' : '!text-gray-200'
										}  !font-semibold `}>
										<p className="!text-sm">
											{pickUpDetail?.timelines![2]?.event || 'Rider picked up package'}
										</p>
										<p className="!text-[10px] !text-gray-400">{pickUpDetail?.fullAddress}</p>
									</div>
								</div>
								{pickUpDetail?.timelines![2] && (
									<img
										src={
											pickUpDetail?.timelines![2].event.includes('failed')
												? '/tick-failed.svg'
												: '/tick-success.svg'
										}
										alt=""
									/>
								)}
							</div>
						</div>
					</DetailsGrid>

					<div className="overflow-auto">
						<table className="w-full">
							<thead className="bg-gray-50 border-b border-gray-200">
								<tr>
									{TABLE_HEAD.map((head) => (
										<th key={head} className="!p-3 !text-xs !font-medium !text-gray-500 text-left">
											{head}
										</th>
									))}
								</tr>
							</thead>
							{isRefetching ? (
								<p className="text-center py-6 mx-auto w-full">Loading...</p>
							) : (
								<tbody>
									{orderData?.deliveryDetails.map(
										({ waybillNumber, recipientName, itemWeight, deliveredAt, status, refId }, index) => {
											const statusType = getStatusType(status);
											const totalCreditsNeeded = calculateCreditsFromWeight(
												parseFloat(itemWeight.replace('kg', '')),
											);

											return (
												<tr
													key={index}
													className="border-b border-gray-100 hover:bg-gray-100 transition-colors">
													<TableData content={waybillNumber} />
													<TableData content={recipientName} />
													<TableData content={itemWeight} />
													<TableData
														content={orderData.paymentMode === 'SUBSCRIPTION' ? totalCreditsNeeded : 'N/A'}
													/>
													<td className="py-5 px-3">
														<StatusCard title={statusType.title} type={statusType.type} />
													</td>
													<TableData
														content={
															status === 'DELIVERED' && deliveredAt
																? moment(deliveredAt).format('DD/MM/YYYY')
																: 'N/A'
														}
													/>
													<td className="!text-xs !text-left !font-semibold !text-[#fe7f1f] !py-5 !px-4">
														{waybillNumber ? (
															<Link to={`/orders/multiple/details/${orderID}/${waybillNumber}`}>
																View Details
															</Link>
														) : (
															<button
																onClick={() => reassignWaybill(refId)}
																disabled={reassigning}
																className={`${reassigning && 'cursor-not-allowed'}`}>
																{reassigning && reassigningWaybill === refId ? 'Retrying' : 'Retry'}
															</button>
														)}
													</td>
												</tr>
											);
										},
									)}
								</tbody>
							)}
						</table>
						<div className="flex justify-between items-center mt-4">
							<div className="text-sm text-gray-500">
								Showing {(deliveryPagination.page - 1) * deliveryPagination.limit + 1} to{' '}
								{Math.min(deliveryPagination.page * deliveryPagination.limit, deliveryPagination.total)} of{' '}
								{deliveryPagination.total} entries
							</div>
							<div className="flex gap-2">
								<button
									onClick={() => {
										setDeliveryPagination((prev) => ({
											...prev,
											page: Math.max(prev.page - 1, 1),
										}));
									}}
									disabled={deliveryPagination.page === 1}
									className="px-3 py-1 border rounded disabled:opacity-50">
									Previous
								</button>
								{Array.from(
									{
										length: Math.ceil(deliveryPagination.total / deliveryPagination.limit),
									},
									(_, i) => i + 1,
								).map((page) => (
									<button
										key={page}
										onClick={() => {
											setDeliveryPagination((prev) => ({
												...prev,
												page,
											}));
										}}
										className={`px-3 py-1 border rounded ${
											deliveryPagination.page === page ? 'bg-[#FE7F1F] text-white' : ''
										}`}>
										{page}
									</button>
								))}
								<button
									onClick={() => {
										setDeliveryPagination((prev) => ({
											...prev,
											page: Math.min(prev.page + 1, Math.ceil(prev.total / prev.limit)),
										}));
									}}
									disabled={
										deliveryPagination.page === Math.ceil(deliveryPagination.total / deliveryPagination.limit)
									}
									className="px-3 py-1 border rounded disabled:opacity-50">
									Next
								</button>
							</div>
						</div>{' '}
					</div>
					<Transition appear show={isRefundOpen} as={React.Fragment}>
						<Dialog as="div" className="relative z-50" onClose={() => setIsRefundOpen(false)}>
							<Transition.Child
								as={React.Fragment}
								enter="ease-out duration-300"
								enterFrom="opacity-0"
								enterTo="opacity-100"
								leave="ease-in duration-200"
								leaveFrom="opacity-100"
								leaveTo="opacity-0">
								<div className="fixed inset-0 bg-black/20 bg-opacity-25" />
							</Transition.Child>

							<div className="fixed inset-0 overflow-hidden">
								<div className="absolute inset-0 overflow-hidden flex items-center justify-center p-4">
									<div className="pointer-events-none flex max-w-[424px] overflow-y-auto !rounded-[20px] overflow-hidden bg-white">
										<Transition.Child
											as={React.Fragment}
											enter="transform transition ease-in-out duration-500 sm:duration-700"
											enterFrom="translate-x-full"
											enterTo="translate-x-0"
											leave="transform transition ease-in-out duration-500 sm:duration-700"
											leaveFrom="translate-x-0"
											leaveTo="translate-x-full">
											<Dialog.Panel className="pointer-events-auto max-w-[424px] w-full overflow-hidden p-7">
												<div className="flex justify-end mb-4">
													<button onClick={() => setIsRefundOpen(false)}>
														<svg
															width="12"
															height="12"
															viewBox="0 0 12 12"
															fill="none"
															xmlns="http://www.w3.org/2000/svg">
															<path
																d="M0.757289 11.2426L5.99993 6M11.2426 0.75736L5.99993 6M5.99993 6L0.757289 0.75736M5.99993 6L11.2426 11.2426"
																stroke="black"
																stroke-width="1.5"
																stroke-linecap="round"
																stroke-linejoin="round"
															/>
														</svg>
													</button>
												</div>

												<div className="flex justify-center">
													<svg
														width="81"
														height="80"
														viewBox="0 0 81 80"
														fill="none"
														xmlns="http://www.w3.org/2000/svg">
														<path
															fill-rule="evenodd"
															clip-rule="evenodd"
															d="M32.5537 12.1398C36.0784 6.01004 44.9224 6.01002 48.447 12.1398L75.2568 58.7656C78.7707 64.8767 74.3595 72.5016 67.3102 72.5016H13.6905C6.64124 72.5016 2.23002 64.8767 5.7439 58.7656L32.5537 12.1398ZM40.5 27.5C41.8807 27.5 43 28.6193 43 30V43.3333C43 44.714 41.8807 45.8333 40.5 45.8333C39.1193 45.8333 38 44.714 38 43.3333V30C38 28.6193 39.1193 27.5 40.5 27.5ZM42.3916 58.336C43.3152 57.3097 43.232 55.729 42.2058 54.8054C41.1795 53.8817 39.5988 53.9649 38.6751 54.9912L38.6418 55.0282C37.7181 56.0545 37.8013 57.6352 38.8276 58.5589C39.8539 59.4825 41.4346 59.3993 42.3583 58.373L42.3916 58.336Z"
															fill="#FF9638"
														/>
													</svg>
												</div>

												<p className="my-4 text-[28px] font-semibold text-[#36394a] text-center">
													Complete Refunds?
												</p>

												<p className="text-sm text-center text-[#818898]">
													Are you sure this customer has been refunded? This is an irreversible action.
												</p>
												{/* Header */}
												<div className="flex items-center !gap-4 justify-between !py-6">
													<button
														onClick={() => setIsRefundOpen(false)}
														className="!bg-transparent !rounded-[10px] !font-bold !py-4 !w-1/2 !flex !items-center !justify-center !text-[#FE7F1F] border border-[#FE7F1F]">
														No
													</button>
													<button
														onClick={() => completeRefund()}
														className="!bg-[#FE7F1F] !rounded-[10px] !font-bold !py-4 !w-1/2 !flex !items-center !justify-center !text-white">
														{isRequesting ? 'Loading...' : 'Yes'}
													</button>
												</div>
											</Dialog.Panel>
										</Transition.Child>
									</div>
								</div>
							</div>
						</Dialog>
					</Transition>

					<Transition
						appear
						show={isOpen && !!localQueueItems && localQueueItems.length > 0}
						as={React.Fragment}>
						<Dialog as="div" className="relative z-50" onClose={() => setIsOpen(false)}>
							<Transition.Child
								as={React.Fragment}
								enter="ease-out duration-300"
								enterFrom="opacity-0"
								enterTo="opacity-100"
								leave="ease-in duration-200"
								leaveFrom="opacity-100"
								leaveTo="opacity-0">
								<div className="fixed inset-0 bg-black/20 bg-opacity-25" />
							</Transition.Child>

							<div className="fixed inset-0 overflow-hidden">
								<div className="absolute inset-0 overflow-hidden flex items-center justify-center p-4">
									<div className="pointer-events-none flex max-w-[513px] h-full overflow-y-auto !rounded-[20px] overflow-hidden bg-white">
										<Transition.Child
											as={React.Fragment}
											enter="transform transition ease-in-out duration-500 sm:duration-700"
											enterFrom="translate-x-full"
											enterTo="translate-x-0"
											leave="transform transition ease-in-out duration-500 sm:duration-700"
											leaveFrom="translate-x-0"
											leaveTo="translate-x-full">
											<Dialog.Panel className="pointer-events-auto w-screen max-w-md flex flex-col justify-between overflow-hidden">
												<div className="flex justify-between items-start border-b border-b-gray-200">
													<div className="!p-6">
														<div className="flex items-center justify-between">
															<p className="!text-xl !font-bold !text-[#272835]">Topup Request</p>
														</div>
														<p className="mt-2 !text-sm text-gray-400">
															See all deliveries that require Top up
														</p>
													</div>
													<button className="p-6" onClick={() => setIsOpen(false)}>
														<XMarkIcon className="w-6 h-6 text-gray-500" aria-hidden="true" />
													</button>
												</div>
												<div className="grow overflow-y-auto p-6">
													<div className="border-b border-b-[#E2E2E2] w-full p-0 mb-3 grid grid-flow-col gap-4 py-0 cursor-pointer overflow-x-auto">
														{['new', 'sent'].map((item: string, index: number) => (
															<button
																key={index}
																className={`pb-3 px-2 focus:outline-none font-semibold text-base transition duration-200 ease-in-out capitalize ${
																	tab === item
																		? 'text-greyscale-700 bg-transparent border-b-2 border-greyscale-700'
																		: 'border-b-2 border-transparent text-greyscale-400'
																}`}
																onClick={() => setTab(item)}>
																{item}
															</button>
														))}
													</div>
													<>
														<p className="text-sm text-gray-500 font-semibold">
															Delivery ({filteredItems.length})
														</p>

														<div className="space-y-4 my-4">
															{filteredItems.map((queue: any, index: number) => (
																<div
																	className="p-4 rounded-[10px] border border-gray-400 relative flex items-center justify-between"
																	key={index}>
																	{/* Left content */}
																	<div>
																		<p className="text-sm font-semibold">
																			Waybill No: {queue?.waybillNumber}
																		</p>
																		<p className="text-xs text-[#666d80] font-semibold mt-1">
																			{queue?.weightDiff && (
																				<span className="mr-4">Extra weight - {queue?.weightDiff}kg</span>
																			)}
																			<span className="">Amount - N{queue.amount.toLocaleString()}</span>
																		</p>
																		{queue?.reason && (
																			<p className="text-xs text-[#666d80] font-semibold mt-1">
																				Reason: {queue?.reason}
																			</p>
																		)}
																	</div>

																	{/* Right buttons */}
																	<div className="flex flex-col gap-2">
																		<button
																			onClick={() => removeQueueData(queue.waybillNumber)}
																			className="text-xs font-medium text-[#2f80ed] border border-[#2f80ed] bg-[#e6f0ff] py-1.5 px-3 rounded-[14px] focus:outline-none focus:ring-2 focus:ring-[#2f80ed] focus:ring-opacity-50 disabled:cursor-not-allowed"
																			disabled={isRemoving}>
																			{isRemoving && removingWaybill === queue.waybillNumber
																				? 'Removing'
																				: 'Remove'}
																		</button>

																		{orderData?.hasPendingTopupRequest && tab !== 'new' && (
																			<button
																				onClick={() => cancelQueueData(queue.waybillNumber)}
																				className="text-xs font-medium text-[#df1c41] bg-[#fff0f3] py-1.5 px-3 rounded-[14px] focus:outline-none focus:ring-2 focus:ring-[#df1c41] focus:ring-opacity-50 border border-red-500 disabled:cursor-not-allowed"
																				disabled={isCancelling}>
																				{isCancelling && cancellingWaybill === queue.waybillNumber
																					? 'Cancelling'
																					: 'Cancel'}
																			</button>
																		)}
																	</div>
																</div>
															))}
														</div>

														<p className="text-sm font-medium text-[#666]">
															Total Weight:{' '}
															<span className="font-bold text-black text-lg">{totalWeight}kg</span>
															<br />
															Total Amount:{' '}
															<span className="font-bold text-black text-lg">
																N{totalAmount.toLocaleString()}
															</span>
														</p>
													</>
												</div>
												{/* Header */}
												{tab === 'new' && (
													<div className="flex items-center !gap-4 justify-end !p-6">
														<button
															onClick={() => {
																requestTopup();
															}}
															disabled={isRequesting}
															className="!bg-[#FE7F1F] !disabled:bg-[#FE7F1F] !rounded-[10px] !font-bold !py-4 !px-[18px] !flex !items-center !justify-center !text-white">
															{isRequesting ? 'Loading...' : 'Send Top up Request'}
														</button>
													</div>
												)}
											</Dialog.Panel>
										</Transition.Child>
									</div>
								</div>
							</div>
						</Dialog>
					</Transition>
					<Transition appear show={isCancelOpen} as={React.Fragment}>
						<Dialog as="div" className="relative z-50" onClose={() => setIsCancelOpen(false)}>
							<Transition.Child
								as={React.Fragment}
								enter="ease-out duration-300"
								enterFrom="opacity-0"
								enterTo="opacity-100"
								leave="ease-in duration-200"
								leaveFrom="opacity-100"
								leaveTo="opacity-0">
								<div className="fixed inset-0 bg-black/20 bg-opacity-25" />
							</Transition.Child>

							<div className="fixed inset-0 overflow-hidden">
								<div className="absolute inset-0 overflow-hidden flex items-center justify-center p-4">
									<div className="pointer-events-none flex max-w-[513px] h-max overflow-y-auto !rounded-[20px] overflow-hidden bg-white">
										<Transition.Child
											as={React.Fragment}
											enter="transform transition ease-in-out duration-500 sm:duration-700"
											enterFrom="translate-x-full"
											enterTo="translate-x-0"
											leave="transform transition ease-in-out duration-500 sm:duration-700"
											leaveFrom="translate-x-0"
											leaveTo="translate-x-full">
											<Dialog.Panel className="pointer-events-auto w-screen max-w-md flex flex-col justify-between overflow-hidden">
												<div className="!p-6">
													<p className="!text-xl !font-bold !text-[#272835] text-center">Cancel Order</p>
													<p className="mt-2 !text-sm text-gray-400 text-center">
														Are you sure you want to cancel order
													</p>
												</div>
												{/* Header */}
												<div className="flex items-center !gap-4 justify-between !p-6">
													<button
														onClick={() => cancelOrder()}
														className="!bg-[#FE7F1F] !rounded-[10px] !font-bold !py-4 !px-[18px] !flex !items-center !justify-center !text-white w-1/2">
														{isCancelling ? 'Cancelling...' : 'Yes, Cancel'}
													</button>
													<button
														onClick={() => setIsCancelOpen(false)}
														className="border !border-[#FF3B30] !rounded-[10px] !font-bold !py-4 !px-[41px] !flex !items-center !justify-center !text-[#FF3B30] w-1/2">
														No
													</button>
												</div>
											</Dialog.Panel>
										</Transition.Child>
									</div>
								</div>
							</div>
						</Dialog>
					</Transition>
					<Transition appear show={isStatusOpen} as={React.Fragment}>
						<Dialog as="div" className="relative z-50" onClose={() => setIsStatusOpen(false)}>
							<Transition.Child
								as={React.Fragment}
								enter="ease-out duration-300"
								enterFrom="opacity-0"
								enterTo="opacity-100"
								leave="ease-in duration-200"
								leaveFrom="opacity-100"
								leaveTo="opacity-0">
								<div className="fixed inset-0 bg-black/20 bg-opacity-25" />
							</Transition.Child>

							<div className="fixed inset-0 overflow-hidden">
								<div className="absolute inset-0 overflow-hidden flex items-center justify-center p-4">
									<div className="pointer-events-none flex max-w-[513px] h-max overflow-y-auto !rounded-[20px] overflow-hidden bg-white">
										<Transition.Child
											as={React.Fragment}
											enter="transform transition ease-in-out duration-500 sm:duration-700"
											enterFrom="translate-x-full"
											enterTo="translate-x-0"
											leave="transform transition ease-in-out duration-500 sm:duration-700"
											leaveFrom="translate-x-0"
											leaveTo="translate-x-full">
											<Dialog.Panel className="pointer-events-auto w-screen max-w-md flex flex-col justify-between overflow-hidden">
												<div className="!p-6">
													<p className="!text-xl !font-bold !text-[#272835] text-center">
														Change Order Status
													</p>
												</div>

												<div className="!px-6 !pb-6">
													<select
														className="select w-full px-3 border border-gray-300 rounded-[10px] focus:border-orange-500 focus:ring-orange-500 text-sm"
														onChange={(e) => setSelectedStatus(e.target.value)}
														value={selectedStatus}>
														<option value="">Select Status</option>
														<option value="IN_HUB">In hub</option>
														<option value="EXECUTING">Executing</option>
														<option value="COMPLETED">Completed</option>
														<option value="CANCELLED">Cancelled</option>
														<option value="PICKUP_FAILED">Pick up Failed</option>
														<option value="PROCESSING">Processing</option>
														<option value="PICKUP_SCHEDULED">Pick up Scheduled</option>
													</select>
												</div>

												<div className="flex items-center !gap-4 justify-between !p-6">
													<button
														onClick={() => updateOrderStatus(selectedStatus)}
														disabled={!selectedStatus || isStatusUpdating}
														className="!bg-[#FE7F1F] !rounded-[10px] !font-bold !py-4 !px-[18px] !flex !items-center !justify-center !text-white w-1/2 disabled:opacity-50 disabled:cursor-not-allowed">
														{isStatusUpdating ? 'Updating...' : 'Update Status'}
													</button>
													<button
														onClick={() => setIsStatusOpen(false)}
														className="border !border-[#FF3B30] !rounded-[10px] !font-bold !py-4 !px-[41px] !flex !items-center !justify-center !text-[#FF3B30] w-1/2">
														Cancel
													</button>
												</div>
											</Dialog.Panel>
										</Transition.Child>
									</div>
								</div>
							</div>
						</Dialog>
					</Transition>
				</div>
			)}
		</AppLayout>
	);
}

const OrderHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 32px;
`;

const OrderInfo = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 40px;
`;

const StatusContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

const RefreshIcon = styled.img`
	margin-top: 4px;
`;

const InfoLabel = styled.span`
	font-size: 14px;
	color: #818898;
`;

const OrderId = styled.span`
	font-size: 20px;
	font-weight: 600;
	color: #272835;
`;

const ActionButtons = styled.div`
	display: flex;
	gap: 8px;
`;

const HubButton = styled.button`
	padding: 16px 28px;
	background: #fe7f1f;
	border-radius: 10px;
	color: white;
	font-weight: bold;
	border: none;
	cursor: pointer;
`;

const GridContainer = styled.div`
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	gap: 24px;
	margin: 48px 0 24px;
`;

const InfoCard = styled.div`
	padding: 32px 24px;
	border: 1px solid rgba(0, 0, 0, 0.1);
	border-radius: 10px;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
`;

const InfoText = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const InfoLabelSmall = styled.p`
	font-size: 12px;
	color: #818898;
`;

const InfoValue = styled.p`
	font-size: 18px;
	color: #272835;
	font-weight: 600;
`;

const InfoValueSmall = styled.p`
	font-size: 10px;
	color: #818898;
`;

const RiderHeader = styled.div`
	display: flex;
	align-items: center;
`;

const DetailsGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 24px;
	margin-bottom: 32px;
`;

const DetailCard = styled.div`
	padding: 20px 32px;
	border: 1px solid rgba(0, 0, 0, 0.1);
	border-radius: 10px;
`;

const TimelineCard = styled(DetailCard)`
	grid-row: span 1;
`;

const DetailHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 12px;
`;

const DetailTitle = styled.p`
	font-size: 14px;
	color: #818898;
	font-weight: 600;
`;

const DetailContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

const DetailText = styled.p`
	font-size: 14px;
	color: #272835;
	margin-bottom: 4px;
`;

const DetailTextBold = styled(DetailText)`
	font-weight: 700;
`;
